import { inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import type { OrganizationStructuredData } from '@core/structured-data';
import { StructuredDataType } from '@core/structured-data';
import {
  JssStateActionTypes,
  JssStateMetaLinkUpdated,
  JssStateMetaTitleUpdated,
  JssStateOrganizationalDataUpdated,
  JssStateUpdate,
} from '@core/jss-routing';
import type {
  ActualSitecoreRouteData,
  InnogyLayoutServiceData,
  RouteDataWithTitle,
} from '@core/jss-models';
import type { ComponentFields, ImageField } from '@core/jss-proxy';
import {
  getFieldValue,
  getPageTitleFromRoute,
  getTreeListValues,
} from '@core/jss-utils';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { filter, map, tap } from 'rxjs/operators';
import { ENVIRONMENT_CONFIG } from '@core/config-angular';
import { EnvConfigEnvironment } from '@core/config-models';

import { LinkService } from './link/link.service';
import { LocationService } from './location/location.service';
import { SeoService } from './seo.service';

@Injectable()
export class MetaEffects {
  private readonly actions$ = inject(Actions);
  private readonly meta = inject(Meta);
  private readonly title = inject(Title);
  private readonly linkService = inject(LinkService);
  private readonly locationService = inject(LocationService);
  private readonly seoService = inject(SeoService);
  readonly #config = inject(ENVIRONMENT_CONFIG);

  setMetaTitle$ = createEffect(() =>
    this.actions$.pipe(
      ofType<JssStateUpdate>(JssStateActionTypes.UPDATE),
      map((jssState) =>
        jssState.payload.sitecore ? jssState.payload.sitecore : null
      ),
      filter((state): state is InnogyLayoutServiceData => state?.route != null),
      tap((state) => {
        this.setMetaStringName('keywords', 'metaKeywords', state.route);
        this.setMetaStringName('description', 'metaDescription', state.route);
        this.setMetaStringProperty(
          'og:description',
          'metaDescription',
          state.route
        );
        this.setMetaStringProperty('og:title', 'openGraphTitle', state.route);
        this.setMetaImageProperty('og:image', 'openGraphImage', state.route);
        this.setMetaRobotsProperty('robots', state.route);
      }),
      map(
        (state) =>
          new JssStateMetaTitleUpdated(
            state.context.pageTitle ||
              getPageTitleFromRoute(state.route as RouteDataWithTitle)
          )
      )
    )
  );

  setCanonical$ = createEffect(() =>
    this.actions$.pipe(
      ofType<JssStateUpdate>(JssStateActionTypes.UPDATE),
      map((jssState) =>
        jssState.payload.sitecore ? jssState.payload.sitecore.route : null
      ),
      filter((route): route is RouteDataWithTitle => route?.fields != null),
      map((route) => this.locationService.getCanonicalUrl(route.fields)),
      tap((canonical) => {
        this.linkService.updateTag({
          rel: 'canonical',
          href: canonical,
        });
      }),
      map((url) => new JssStateMetaLinkUpdated({ type: 'canonical', url }))
    )
  );

  setJssStateMetaTitle$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<JssStateMetaTitleUpdated>(
          JssStateActionTypes.META_TITLE_UPDATED
        ),
        tap((action) => {
          this.title.setTitle(action.payload);
          this.meta.updateTag({
            property: 'og:title',
            content: action.payload,
          });
        })
      ),
    { dispatch: false }
  );

  setOrganizationData$ = createEffect(() =>
    this.actions$.pipe(
      ofType<JssStateUpdate>(JssStateActionTypes.UPDATE),
      map((jssState) => jssState.payload.sitecore ?? null),
      filter((state): state is InnogyLayoutServiceData => state?.route != null),
      tap((state) => this.setOrganizationData(state.route)),
      map(() => new JssStateOrganizationalDataUpdated())
    )
  );

  private setMetaStringName(
    name: string,
    field: string,
    route?: ActualSitecoreRouteData
  ) {
    this.meta.updateTag({
      name,
      content: getFieldValue<string>(route?.fields, field, ''),
    });
  }

  private setMetaStringProperty(
    property: string,
    field: any,
    route?: ActualSitecoreRouteData
  ) {
    this.meta.updateTag({
      property,
      content: getFieldValue<string>(route?.fields, field, ''),
    });
  }

  private setMetaImageProperty(
    property: string,
    field: any,
    route?: ActualSitecoreRouteData
  ) {
    this.meta.updateTag({
      property,
      content: getFieldValue<ImageField>(route?.fields, field)?.src ?? '',
    });
  }

  private setMetaRobotsProperty(field: any, route?: ActualSitecoreRouteData) {
    const isProduction =
      this.#config.environment === EnvConfigEnvironment.Production;
    const value = getFieldValue<string>(route?.fields, field, '');
    const robots = value.split(/[ ,]+/).join(',');

    this.meta.updateTag({
      name: 'robots',
      /**
       * There is a chance that some cloud-config-f***-up causes the lower environments to be indexable.
       * We add this check here as an extra measure to make sure these lower environment pages do not get indexed.
       */
      content: isProduction ? robots : 'noindex,nofollow',
    });
  }

  private setOrganizationData(route?: ActualSitecoreRouteData) {
    if (!route) {
      return;
    }
    const data = this.createOrganizationObject(route.fields as ComponentFields);
    if (data) {
      this.seoService.injectStructuredData(data);
    }
  }

  private createOrganizationObject(
    fields?: ComponentFields
  ): OrganizationStructuredData | undefined {
    if (!fields) {
      return;
    }
    const name = getFieldValue<string>(fields, 'organizationName');
    const sameAs = getTreeListValues(fields, 'organizationSameAs').map((item) =>
      getFieldValue<string>(item.fields, 'value', '')
    );

    if (!name) {
      return;
    }

    return {
      '@context': 'https://schema.org',
      '@type': StructuredDataType.Organization,
      name,
      url: getFieldValue<string>(fields, 'organizationUrl'),
      logo: getFieldValue<string>(fields, 'organizationLogo'),
      sameAs: sameAs.length > 0 ? sameAs : undefined,
      location: getFieldValue<string>(fields, 'organizationLocation'),
      foundingDate: getFieldValue<string>(fields, 'organizationFoundingDate'),
      telephone: getFieldValue<string>(fields, 'organizationTelephone'),
    };
  }
}
